import { useEffect, useMemo, useState } from 'react'
import { X } from 'lucide-react'
import { useCompanyUsers, useDeleteUser } from '../../hooks/api/useUsers'
import { useTeamInvitations, useCreateTeamInvitation, useResendTeamInvitation } from '../../hooks/api/useTeamInvitations'
import { useDeleteCompany, useUpdateCompany, type CompanySummary } from '../../hooks/api/useCompanies'
import { Select } from '../ui/Select'

type PanelMode = 'overview' | 'edit' | 'permissions'

interface OrganizationDetailPanelProps {
  company: CompanySummary | null
  isOpen: boolean
  onClose: () => void
  currentAdminId: string
  mode: PanelMode
  onModeChange: (mode: PanelMode) => void
}

export const OrganizationDetailPanel = ({
  company,
  isOpen,
  onClose,
  currentAdminId,
  mode,
  onModeChange
}: OrganizationDetailPanelProps) => {
  const companyId = company?.id

  const { data: members = [], isLoading: membersLoading } = useCompanyUsers(companyId)
  const { data: invitations = [], isLoading: invitationsLoading } = useTeamInvitations(companyId)
  const createInvitation = useCreateTeamInvitation()
  const resendInvitation = useResendTeamInvitation()
  const deleteCompanyMutation = useDeleteCompany()
  const deleteUserMutation = useDeleteUser()
  const updateCompanyMutation = useUpdateCompany()

  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteName, setInviteName] = useState('')
  const [inviteRole, setInviteRole] = useState<'manager' | 'employee'>('manager')
  const [includeDeviceSetup, setIncludeDeviceSetup] = useState(false)
  const [deviceOsType, setDeviceOsType] = useState<'windows' | 'mac' | 'linux'>('windows')
  const [deviceName, setDeviceName] = useState('')
  const [editName, setEditName] = useState('')
  const [editSlug, setEditSlug] = useState('')
  const [editTier, setEditTier] = useState<'essentials' | 'enhanced' | 'premium'>(company?.subscription_tier ?? 'essentials')
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [resendingInvitationId, setResendingInvitationId] = useState<string | null>(null)
  const [copiedInvitationId, setCopiedInvitationId] = useState<string | null>(null)

  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => {
      const priority = { admin: 0, manager: 1, employee: 2, billing: 3 }
      return (priority[a.role] ?? 4) - (priority[b.role] ?? 4)
    })
  }, [members])

  const formatDate = (value?: string | null) => {
    if (!value) return '—'
    return new Date(value).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  useEffect(() => {
    if (isOpen) {
      setFeedback(null)
      setInviteEmail('')
      setInviteName('')
      setInviteRole('manager')
      setIncludeDeviceSetup(false)
      setDeviceOsType('windows')
      setDeviceName('')
      setEditName(company?.name ?? '')
      setEditSlug(company?.slug ?? '')
      setEditTier((company?.subscription_tier as 'essentials' | 'enhanced' | 'premium') ?? 'essentials')
      onModeChange('overview')
    }
  }, [isOpen, companyId, company?.name, company?.slug, company?.subscription_tier, onModeChange])

  if (!isOpen || !company || !companyId) {
    return null
  }

  const handleRemoveMember = async (memberId: string, memberRole: string) => {
    if (!companyId) return
    if (memberId === currentAdminId) {
      setFeedback({ type: 'error', message: 'You cannot remove yourself from this organization.' })
      return
    }
    if (memberRole === 'admin') {
      setFeedback({ type: 'error', message: 'Admin accounts must be managed from the admin user console.' })
      return
    }

    try {
      await deleteUserMutation.mutateAsync(memberId)
      setFeedback({ type: 'success', message: 'Member removed successfully.' })
    } catch (error) {
      console.error('Failed to remove member', error)
      setFeedback({ type: 'error', message: 'Failed to remove member. Please try again.' })
    }
  }

  const handleDeleteOrganization = async () => {
    if (!companyId) return

    // Check if company has Kaseya integration
    const hasKaseya = company?.kaseya_organization_id || company?.kaseya_group_id

    const confirmMessage = hasKaseya
      ? `Are you sure you want to delete ${company?.name}?\n\nThis will delete all associated users, devices, and tickets from Helpdesk 411.\n\n⚠️ NOTE: The organization will remain in Kaseya VSA and must be deleted manually if needed (Kaseya API does not support deletion).\n\nThis action cannot be undone.`
      : `Are you sure you want to delete ${company?.name}? This will remove all associated users, devices, and tickets. This action cannot be undone.`

    const confirmed = window.confirm(confirmMessage)
    if (!confirmed) return

    try {
      await deleteCompanyMutation.mutateAsync(companyId)
      setFeedback({
        type: 'success',
        message: hasKaseya
          ? 'Organization deleted from Helpdesk 411. Note: Kaseya record remains (delete manually if needed).'
          : 'Organization deleted.'
      })
      onClose()
    } catch (error) {
      console.error('Failed to delete organization', error)
      setFeedback({ type: 'error', message: 'Failed to delete organization. Please try again.' })
    }
  }

  const handleSaveOrganization = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!companyId) return

    if (!editName.trim() || !editSlug.trim()) {
      setFeedback({ type: 'error', message: 'Name and slug are required.' })
      return
    }

    try {
      await updateCompanyMutation.mutateAsync({
        id: companyId,
        name: editName.trim(),
        slug: editSlug.trim(),
        subscription_tier: editTier as CompanySummary['subscription_tier']
      })
      setFeedback({ type: 'success', message: 'Organization updated successfully.' })
      onModeChange('overview')
    } catch (error) {
      console.error('Failed to update organization', error)
      setFeedback({ type: 'error', message: 'Failed to update organization. Please try again.' })
    }
  }

  const handleInvite = async (event: React.FormEvent) => {
    event.preventDefault()
    setFeedback(null)

    if (!inviteEmail || !inviteName) {
      setFeedback({ type: 'error', message: 'Full name and email are required.' })
      return
    }

    try {
      await createInvitation.mutateAsync({
        company_id: companyId,
        invited_by_user_id: currentAdminId,
        email: inviteEmail,
        full_name: inviteName,
        role: inviteRole,
        include_device_setup: includeDeviceSetup,
        device_os_type: includeDeviceSetup && inviteRole === 'employee' ? deviceOsType : undefined,
        device_name: includeDeviceSetup && deviceName ? deviceName : undefined
      })
      setInviteEmail('')
      setInviteName('')
      setInviteRole('manager')
      setIncludeDeviceSetup(false)
      setDeviceOsType('windows')
      setDeviceName('')
      setFeedback({ type: 'success', message: 'Invitation sent successfully.' })
    } catch (error) {
      console.error('Failed to send invitation', error)
      setFeedback({ type: 'error', message: 'Failed to send invitation. Please try again.' })
    }
  }

  const handleResendInvitation = async (invitationId: string) => {
    setResendingInvitationId(invitationId)
    setFeedback(null)

    try {
      await resendInvitation.mutateAsync(invitationId)
      setFeedback({ type: 'success', message: 'Invitation email resent successfully.' })
    } catch (error) {
      console.error('OrganizationDetailPanel: failed to resend invitation', error)
      setFeedback({ type: 'error', message: 'Failed to resend invitation. Please try again.' })
    } finally {
      setResendingInvitationId(null)
    }
  }

  const handleCopyInvitationLink = async (invitationId: string, token: string) => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      return
    }

    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    const inviteUrl = `${origin}/accept-invite?token=${token}`

    try {
      await navigator.clipboard.writeText(inviteUrl)
      setCopiedInvitationId(invitationId)
      setTimeout(() => setCopiedInvitationId(null), 2000)
    } catch (error) {
      console.error('OrganizationDetailPanel: failed to copy invitation link', error)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/40 backdrop-blur-sm">
      <div className="relative h-full w-full max-w-4xl overflow-y-auto bg-white shadow-xl">
        <div className="sticky top-0 flex flex-col gap-4 border-b border-gray-200 bg-white px-8 py-6 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{company.name}</h2>
            <p className="text-sm text-gray-500">{company.slug}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex rounded-full border border-gray-200 bg-gray-100 p-1 text-xs font-medium text-gray-600">
              {(['overview', 'edit', 'permissions'] as PanelMode[]).map(tab => (
                <button
                  key={tab}
                  onClick={() => onModeChange(tab)}
                  className={`rounded-full px-3 py-1 capitalize transition-colors ${
                    mode === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab === 'permissions' ? 'Members' : tab}
                </button>
              ))}
            </div>
            <button
              onClick={handleDeleteOrganization}
              disabled={deleteCompanyMutation.isPending}
              className="rounded-full border border-red-200 px-4 py-2 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70"
            >
              Delete Organization
            </button>
            <button
              onClick={onClose}
              className="rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="space-y-10 px-8 py-6">
          <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Subscription</p>
              <p className="mt-2 text-lg font-semibold text-gray-900 capitalize">
                {company.subscription_tier || 'Not set'}
              </p>
            </div>
            <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Users</p>
              <p className="mt-2 text-lg font-semibold text-gray-900">{company.user_count}</p>
            </div>
            <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Devices</p>
              <p className="mt-2 text-lg font-semibold text-gray-900">{company.device_count}</p>
            </div>
            <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Tickets</p>
              <p className="mt-2 text-lg font-semibold text-gray-900">{company.ticket_count}</p>
            </div>
          </section>

          {mode === 'overview' && (
            <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900">Overview</h3>
              <p className="mt-2 text-sm text-gray-500">
                Use the tabs above to edit subscription details or manage member access for this organization.
              </p>
              <dl className="mt-4 grid gap-3 sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Stripe Customer ID</dt>
                  <dd className="text-sm text-gray-900">{company.stripe_customer_id || 'Not linked'}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Created</dt>
                  <dd className="text-sm text-gray-900">
                    {company.created_at ? new Date(company.created_at).toLocaleString() : '—'}
                  </dd>
                </div>
              </dl>
            </section>
          )}

          {mode === 'edit' && (
            <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900">Edit Organization</h3>
              <p className="mt-1 text-sm text-gray-500">Update core organization details and subscription tier.</p>

              <form onSubmit={handleSaveOrganization} className="mt-4 space-y-3">
                <div>
                  <label className="text-xs font-medium uppercase tracking-wide text-gray-500">Name</label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(event) => setEditName(event.target.value)}
                    className="mt-1 w-full rounded-full border border-gray-300 px-4 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium uppercase tracking-wide text-gray-500">Slug</label>
                  <input
                    type="text"
                    value={editSlug}
                    onChange={(event) => setEditSlug(event.target.value)}
                    className="mt-1 w-full rounded-full border border-gray-300 px-4 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium uppercase tracking-wide text-gray-500">Subscription Tier</label>
                  <Select
                    value={editTier}
                    onChange={(event) => setEditTier(event.target.value as 'essentials' | 'enhanced' | 'premium')}
                    className="mt-1 w-full capitalize"
                  >
                    <option value="essentials">Essentials</option>
                    <option value="enhanced">Enhanced</option>
                    <option value="premium">Premium</option>
                  </Select>
                </div>

                {feedback && (
                  <div
                    className={`rounded-lg px-4 py-2 text-xs ${
                      feedback.type === 'success'
                        ? 'border border-green-200 bg-green-50 text-green-700'
                        : 'border border-red-200 bg-red-50 text-red-600'
                    }`}
                  >
                    {feedback.message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={updateCompanyMutation.isPending}
                  className="w-full rounded-full bg-gray-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {updateCompanyMutation.isPending ? 'Saving…' : 'Save Changes'}
                </button>
              </form>
            </section>
          )}

          {mode === 'permissions' && (
            <>
              <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="flex flex-col gap-4 border-b border-gray-100 pb-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Members</h3>
                    <p className="text-sm text-gray-500">Managers and employees associated with this organization.</p>
                  </div>
                </div>

                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Role</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                        <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {membersLoading ? (
                        <tr>
                          <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">
                            Loading members...
                          </td>
                        </tr>
                      ) : sortedMembers.length > 0 ? (
                        sortedMembers.map(member => (
                          <tr key={member.id}>
                            <td className="px-4 py-3 text-sm text-gray-900">{member.full_name || '—'}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">{member.email}</td>
                            <td className="px-4 py-3 text-sm capitalize text-gray-500">{member.role}</td>
                            <td className="px-4 py-3 text-sm">
                              <span
                                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                                  member.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {member.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-sm">
                              {member.id === currentAdminId ? (
                                <span className="text-xs text-gray-400">You</span>
                              ) : (
                                <button
                                  onClick={() => handleRemoveMember(member.id, member.role)}
                                  disabled={deleteUserMutation.isPending}
                                  className="rounded-full border border-gray-300 px-3 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-70"
                                >
                                  Remove
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">
                            No members yet. Send an invitation below to add staff.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900">Send Invitation</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Invite managers or employees to this organization. Invitations expire after 7 days.
                </p>

                <form onSubmit={handleInvite} className="mt-4 space-y-3">
                  <div>
                    <label className="text-xs font-medium uppercase tracking-wide text-gray-500">Full name</label>
                    <input
                      type="text"
                      value={inviteName}
                      onChange={(event) => setInviteName(event.target.value)}
                      className="mt-1 w-full rounded-full border border-gray-300 px-4 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
                      placeholder="Alex Johnson"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium uppercase tracking-wide text-gray-500">Email</label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(event) => setInviteEmail(event.target.value)}
                      className="mt-1 w-full rounded-full border border-gray-300 px-4 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
                      placeholder="alex@example.com"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium uppercase tracking-wide text-gray-500">Role</label>
                    <Select
                      value={inviteRole}
                      onChange={(event) => setInviteRole(event.target.value as 'manager' | 'employee')}
                      className="mt-1 w-full capitalize"
                    >
                      <option value="manager">Manager</option>
                      <option value="employee">Employee</option>
                    </Select>
                  </div>

                  {/* Device Setup Section */}
                  <div className="border-t border-gray-200 pt-3 mt-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeDeviceSetup}
                        onChange={(e) => setIncludeDeviceSetup(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-2 focus:ring-gray-900"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">Include device setup link</span>
                        <p className="text-xs text-gray-500">
                          {inviteRole === 'manager'
                            ? 'Manager will choose their OS when accepting the invitation.'
                            : 'Employee will receive a pre-configured installer link for their device.'}
                        </p>
                      </div>
                    </label>

                    {includeDeviceSetup && inviteRole === 'employee' && (
                      <div className="mt-3 space-y-2 ml-7">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Operating System</label>
                          <Select
                            value={deviceOsType}
                            onChange={(e) => setDeviceOsType(e.target.value as 'windows' | 'mac' | 'linux')}
                            className="w-full"
                          >
                            <option value="windows">Windows</option>
                            <option value="mac">macOS</option>
                            <option value="linux">Linux</option>
                          </Select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Device Name (Optional)</label>
                          <input
                            type="text"
                            value={deviceName}
                            onChange={(e) => setDeviceName(e.target.value)}
                            className="w-full rounded-full border border-gray-300 px-4 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
                            placeholder="Work Laptop"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {feedback && (
                    <div
                      className={`rounded-lg px-4 py-2 text-xs ${
                        feedback.type === 'success'
                          ? 'border border-green-200 bg-green-50 text-green-700'
                          : 'border border-red-200 bg-red-50 text-red-600'
                      }`}
                    >
                      {feedback.message}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={createInvitation.isPending}
                    className="w-full rounded-full bg-gray-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {createInvitation.isPending ? 'Sending…' : 'Send Invite'}
                  </button>
                </form>
              </section>

              <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="flex items-center justify-between border-b border-gray-100 pb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Pending Invitations</h3>
                    <p className="text-sm text-gray-500">Monitor outstanding invites and resend if needed.</p>
                  </div>
                </div>

                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Role</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Last sent</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Expires</th>
                        <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {invitationsLoading ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-500">
                            Loading invitations...
                          </td>
                        </tr>
                      ) : invitations.filter(invitation => invitation.status !== 'accepted').length > 0 ? (
                        invitations
                          .filter(invitation => invitation.status !== 'accepted')
                          .map(invitation => (
                          <tr key={invitation.id}>
                            <td className="px-4 py-3 text-sm text-gray-900">{invitation.full_name}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">{invitation.email}</td>
                            <td className="px-4 py-3 text-sm capitalize text-gray-500">{invitation.role}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">{invitation.status}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">{formatDate(invitation.last_sent_at)}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">{formatDate(invitation.expires_at)}</td>
                            <td className="px-4 py-3 text-sm text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleCopyInvitationLink(invitation.id, invitation.token)}
                                  className="rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100"
                                >
                                  {copiedInvitationId === invitation.id ? 'Copied!' : 'Copy link'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleResendInvitation(invitation.id)}
                                  disabled={resendingInvitationId === invitation.id}
                                  className="rounded-full border border-gray-900 px-3 py-1 text-xs font-medium text-gray-900 transition-colors hover:bg-gray-900 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                  {resendingInvitationId === invitation.id ? 'Resending…' : 'Resend email'}
                                </button>
                              </div>
                            </td>
                          </tr>
                          ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-500">
                            No active invitations.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
